import Accordion from "../../components/accordion/accordion";
import styles from "./main.module.scss";
import { ReactComponent as Scroll } from "../../assets/scroll.svg";
import { ReactComponent as Arrow } from "../../assets/arrow.svg";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueries, useQuery } from "@tanstack/react-query";
import api from "../../utils/api";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
import { newsDates } from "../../utils/formatDate";
import { getAllExplore } from "./request";
import { RefObject, useEffect, useRef } from "react";
import { Spin } from "antd";
import { routes } from "../../routes";
import { motion } from "framer-motion";
import WhySchool from "./whySchool";
import MotionBox from "../../custom/motionBox";
import { getAllModeOfStudy } from "../../utils/auth_request";

const normalizeForMatch = (value?: string | null) =>
  value?.trim().toLowerCase().replace(/[^a-z0-9]/g, "") ?? "";

// A program has its modes of study fixed on it as a comma-separated string
// (e.g. "FULL-TIME,PART-TIME ICE") - not picked from the full master list.
const getProgramModeOfStudyOptions = (
  program: ProgramData,
  activeModeOfStudy: getAllModeOfStudy[] | undefined,
) => {
  const programModeNames = (program.modeofStudies ?? "")
    .split(",")
    .map((name) => normalizeForMatch(name))
    .filter(Boolean);

  if (!programModeNames.length) return [];

  return (
    activeModeOfStudy?.filter((mode) =>
      programModeNames.includes(normalizeForMatch(mode.name)),
    ) ?? []
  );
};

const getModeOfStudyButtonClass = (
  modeName: string,
  styles: Record<string, string>,
) => {
  const normalized = normalizeForMatch(modeName);
  if (normalized === "fulltime") return styles.applyFullTime;
  if (normalized === "parttimeice") return styles.applyPartTime;
  return styles.applyDefault;
};

const Home = () => {
  const facultySectionRef = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  const modeOfStudyQuery = useQuery({
    queryKey: ["get-mode-of-study"],
    queryFn: getAllModeOfStudy,
    staleTime: Infinity,
  });

  const activeModeOfStudy = modeOfStudyQuery.data?.data?.filter(
    (mode) => mode.activeStatus === true,
  );

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  const faqQuery = useQuery({
    queryKey: ["get-faq"],
    queryFn: async () =>
      (await api.get("/HomePage/homePage/GetAllFaq"))?.data as GetFaqResponse,
  });

  const facultyQuery = useQuery({
    queryKey: ["get-faculties"],
    queryFn: async () =>
      (await api.get("/Academics/GetAllCategoryAsync"))
        ?.data as GetCategoriesResponse,
  });

  const faculties = facultyQuery.data?.data as Category[];
  const faqTitle = faqQuery.data?.data as FaqTitle;

  const [programQuery, whyQuery, faqItemQuery, newsQuery, exploreQuery] =
    useQueries({
      queries: [
        {
          queryKey: ["get-programs"],
          queryFn: async () =>
            (
              await api.get(
                `/Utilities/Utilities/GetAllProgramDetails`,
              )
            )?.data as ProgramDataResponse,
        },
        {
          queryKey: ["get-why-school"],
          queryFn: async () =>
            (
              await api.get(
                "/HomePage/HomePage/GetAllWhySchoolItemByWhyId?Id=1",
              )
            )?.data as GetWhyUsResponse,
        },
        {
          queryKey: ["get-faq-items"],
          queryFn: async () =>
            (
              await api.get(
                `/HomePage/homePage/GetFaqItemsByFaqId?Id=${faqTitle?.id}`,
              )
            )?.data as GetFaqItemResponse,
          enabled: !!faqTitle?.id,
        },
        {
          queryKey: ["get-news-and-events"],
          queryFn: async () =>
            (await api.get("/HomePage/HomePage/GetAllNewEvent"))
              ?.data as GetEventsResponse,
        },
        {
          queryKey: ["get-explore"],
          queryFn: getAllExplore,
        },
      ],
    });

  const programs = programQuery.data?.data?.filter(
    (item) => item?.activeStatus === true,
  ) as ProgramData[];
  

  const whys = whyQuery.data?.data?.filter(
    (item) => item?.activeStatus === true,
  ) as WhyUs[];

  const events = newsQuery.data?.data?.filter(
    (item) => item?.activeStatus === true,
  ) as Event[];

  const faqItems = faqItemQuery.data?.data?.filter(
    (item) => item?.activeStatus === true,
  ) as Faq[];

  const exploreData =
    exploreQuery?.data?.data?.filter((item) => item?.isActive === true) ?? [];

  const isLoadingExplore = exploreQuery?.isLoading;
  const isExploreError = exploreQuery?.isError;
  const exploreError = exploreQuery?.error;

  const scrollToFaculty = (ref: RefObject<HTMLDivElement>) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const getProgramsForFaculty = (faculty: Category) => {
    const exactMatches =
      programs?.filter(
        (program) =>
          program.categoryCode === faculty.categoryCode ||
          program.categoryCodeName === faculty.name,
      ) ?? [];

    if (exactMatches.length > 0) {
      return exactMatches;
    }

    // Some environments return a single faculty with a slightly different
    // category code than the program payload. In that case, show the programs
    // instead of rendering an empty accordion.
    if ((faculties?.length ?? 0) === 1) {
      return programs ?? [];
    }

    return [];
  };

  if (isLoadingExplore)
    return (
      <div className={styles.loader}>
        <Spin size="large" />
      </div>
    );

  if (isExploreError) return <div>{exploreError?.message}</div>;

  return (
    <div>
      <section id="admission" className={styles.hero}>
        <h1 color='black'>
        Shaping the future of the energy industry through world-class technical education, hands-on training, and innovative research.
        </h1>

        <motion.button
          whileHover={{
            scale: 1.1,
          }}
          whileTap={{ scale: 0.95 }}
          transition={{ type: "spring", stiffness: 300 }}
          onClick={() => scrollToFaculty(facultySectionRef)}
        >
          Apply Now
        </motion.button>
      </section>

      <section ref={facultySectionRef} className={styles.programmes}>
        <div className={styles.heading}>
          <h2>{exploreData[0]?.title}</h2>
          <span className="underline"></span>
        </div>

        <br />

        <div
          className={styles.body}
          dangerouslySetInnerHTML={sanitizeHtml(exploreData[0]?.description)}
        />

        <br />

        <div className={styles.flexColumn}>
          {faculties &&
            faculties?.map((item, index) => (
              <Accordion
                title={item.name}
                key={item.id}
                defaultOpen={index === 0}
                children={
                  <>
                    {getProgramsForFaculty(item)?.map((program) => {
                      const programModeOfStudyOptions =
                        getProgramModeOfStudyOptions(program, activeModeOfStudy);

                      return (
                        <div key={program.id} className={styles.course}>
                          <p>{program?.programName}</p>
                          <div className="small-gap">
                            {programModeOfStudyOptions.length > 0 ? (
                              programModeOfStudyOptions.map((mode) => (
                                <button
                                  key={mode.id}
                                  className={getModeOfStudyButtonClass(
                                    mode.name,
                                    styles,
                                  )}
                                  onClick={() =>
                                    navigate(
                                      `/register/${program?.programTypeId}/${program?.programId}/${program?.id}?modeOfStudyId=${mode.id}`,
                                    )
                                  }
                                >
                                  Apply - {mode.name}
                                </button>
                              ))
                            ) : (
                              <button
                                className={styles.applyDefault}
                                onClick={() =>
                                  navigate(
                                    `/register/${program?.programTypeId}/${program?.programId}/${program?.id}`,
                                  )
                                }
                              >
                                Apply
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </>
                }
              />
            ))}
        </div>
      </section>

      <WhySchool whys={whys} />

      <section className={styles.faq}>
        <div className={styles.faqHeading}>
          <h2>{faqTitle?.name}</h2>
          <br />

          <span>
            <div
              className={styles.faqDescription}
              dangerouslySetInnerHTML={sanitizeHtml(faqTitle?.description)}
            />
            <Link to={routes.main.contactUs} className={styles.link}>
              Contact our friendly team
            </Link>
          </span>
        </div>

        <div className={styles.answers}>
          {faqItems &&
            faqItems.length > 0 &&
            faqItems.map((item, index) => (
              <>
                <details
                  className={styles.details}
                  open={index === 0}
                  key={item.id}
                >
                  <summary>
                    <span>{item.question}</span>
                    <Arrow />
                  </summary>
                  <p>{item.answer}</p>
                </details>

                <hr className={styles.line} />
              </>
            ))}
          <Scroll className={styles.scroll} />
        </div>
      </section>

      <section className={styles.news}>
        <div className={styles.heading}>
          <h2>News and Events</h2>
          <span className="underline"></span>
        </div>

        <br />

        <div className={styles.posts}>
          {events &&
            events?.map((item) => (
              <MotionBox key={item?.id}>
                <div className={styles.newsImg}>
                  <img className={styles.image} src={item?.imageUrl} alt="" />
                </div>
                <h4>{item.title}</h4>
                <p>{newsDates(item)}</p>
                <div dangerouslySetInnerHTML={sanitizeHtml(item.description)} />
              </MotionBox>
            ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
